import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { CompaniesService } from 'services';
import { GetCompanyMetricHook, GetCompanyMetricParams, UseGetCompanyMetric, UseGetCompanyMetricParams } from './types';

const getCompanyMetric = async <T = unknown>(params: GetCompanyMetricParams): Promise<T | null> => {
  const { companyId, metricName, measurementDateSlug } = params;

  if (isNil(companyId)) {
    return null;
  }

  const companiesService = new CompaniesService();

  try {
    const metric = await companiesService.getCompanyMetric<T>(companyId, metricName, measurementDateSlug);

    if (!metric) {
      throw new Error(`Failed to fetch ${metricName} metric: No data returned`);
    }

    return metric;
  } catch (error) {
    // Re-throw the error so React Query can handle it properly
    // This prevents infinite loops while still allowing error handling
    throw error instanceof Error ? error : new Error(`Failed to fetch ${metricName} metric: ${String(error)}`);
  }
};

const useGetCompanyMetric: UseGetCompanyMetric = <T = unknown>(params: UseGetCompanyMetricParams) => {
  const { company, metricName, measurementDate, shouldQueryAutomatically = true } = params;

  const companyId = company?.id;
  const measurementDateSlug = measurementDate?.slug;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<GetCompanyMetricHook<T>['data'], Error>({
    enabled: !isNil(companyId) && shouldQueryAutomatically,
    queryFn: () => getCompanyMetric<T>({ companyId: companyId as number, metricName, measurementDateSlug }),
    queryKey: ['company-metric', companyId, metricName, measurementDateSlug],
    retry: (failureCount, error) => {
      // Don't retry if it's a client error (4xx) or if we've already retried 2 times
      if (error && 'response' in error) {
        const { response } = error as { response?: { status?: number } };
        if (response?.status && response.status >= 400 && response.status < 500) {
          return false; // Don't retry client errors
        }
      }
      return failureCount < 2; // Retry up to 2 times for server errors
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (formerly cacheTime)
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetCompanyMetric;
